import React, { useState } from 'react';
import { useItems, Item } from '../hooks/useItems';
import { useCategories, Category } from '../hooks/useCategories';
import ItemTable from './Items/components/ItemTable';
import ItemFormDrawer from './Items/components/ItemFormDrawer';
import { Button } from '../components/ui/Button';

const ItemsPage: React.FC = () => {
  const { items, isLoading, error, deleteItem } = useItems();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const filteredItems = items.filter((item: Item) => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || item.category_id === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const { categories, error: categoriesError, isLoading: categoriesLoading } = useCategories('item');
  


  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-red-800">Error loading items</h3>
          <p className="mt-1 text-sm text-red-700">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white">Items</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Manage your product and service catalog
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <label htmlFor="search" className="sr-only">
            Search items
          </label>
          <input
            id="search"
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-xl border border-gray-300 bg-white pl-9 pr-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
          />
        </div>
        <div className="sm:w-48">
          <label htmlFor="category-filter" className="sr-only">
            Filter by category
          </label>
          <select
            id="category-filter"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            role="combobox"
            className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
          >
            <option value="">All Categories</option>
            {categories.map((category: Category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
        <Button
          onClick={() => {
            setSelectedItem(null);
            setIsDrawerOpen(true);
          }}
          className="inline-flex items-center justify-center rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
          aria-label="Add new item"
        >
          Add Item
        </Button>
      </div>

      {/* Items Table */}
      <ItemTable 
        items={filteredItems} 
        isLoading={isLoading}
        onEdit={(item: Item) => {
          console.log('ItemsPage: onEdit called with item:', item);
          console.log('ItemsPage: item.id =', item?.id);
          console.log('ItemsPage: item keys =', Object.keys(item || {}));
          setSelectedItem(item);
          setIsDrawerOpen(true);
        }}
        onDelete={async (itemId: string) => {
          if (window.confirm('Are you sure you want to delete this item?')) {
            try {
              await deleteItem(itemId);
            } catch (error) {
              console.error('Failed to delete item:', error);
            }
          }
        }}
      />

      {/* Add/Edit Item Drawer */}
      <ItemFormDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedItem(null);
        }}
        item={selectedItem}
        categories={categories}
      />
    </div>
  );
};

export default ItemsPage; 